// The invoice-number box of every "Generate invoice" dialog, through the one invoice-number service (DUP-23).
//
// While the shared counter is off (today - owner, 26 Sep 2026) it is the plain reference box each page always
// had, and the typed reference is formatted as that page always did (`numbering`: prefix / mode / blank).
// With the counter on it is prefilled with the next free number and has a "print again" tick box.
import React, { useEffect, useState } from 'react';
import { Field, Input, Checkbox } from '../ui/Field/Field';
import { INVOICE_PREFIX, invoiceCounterOn, issueInvoiceNumbers, parseInvoiceNumber, suggestNextInvoiceNumber } from '../services/invoiceNumbers';

/**
 * `open`: the dialog is showing (the counter's suggestion is read when it opens); change `refresh` to read it
 * again. `numbering`: the page's own numbering while the counter is off - {prefix, mode, blank}.
 */
export function useInvoiceNumber(open, refresh = 0, numbering = {}) {
    const [value, setValue] = useState('');
    const [suggested, setSuggested] = useState('');
    const [reissue, setReissue] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !invoiceCounterOn()) return undefined;   // off: nothing is read, the typed value stays
        let live = true;
        setReissue(false);
        setError('');
        suggestNextInvoiceNumber().then((next) => {
            if (!live) return;
            const n = next ? String(parseInvoiceNumber(next)) : '';
            setSuggested(n);
            setValue(n);
        });
        return () => { live = false; };
    }, [open, refresh]);

    /** The numbers of `count` invoices (the dialog shows why when they cannot be given). */
    const claim = async (count = 1, by = '') => {
        setError('');
        const typed = String(value || '').trim();
        const counterStart = !reissue && typed && typed === suggested ? '' : typed;
        try {
            return await issueInvoiceNumbers(count, { ...numbering, typed: invoiceCounterOn() ? counterStart : typed, reissue, by });
        } catch (err) {
            setError(err?.message || 'The invoice number could not be issued.');
            throw err;
        }
    };

    return { value, setValue, reissue, setReissue, error, setError, suggested, claim };
}

/**
 * label / placeholder / prefix / optional: how the page's reference box looked (used while the counter is off).
 */
export default function InvoiceNumberField({ state, label = 'Reference number', placeholder = 'e.g. 100', prefix = INVOICE_PREFIX, optional = false, count = 1 }) {
    if (!invoiceCounterOn()) {
        return (
            <Field label={label} optional={optional} error={state.error || undefined}>
                <Input
                    prefix={prefix || undefined}
                    value={state.value}
                    onChange={(e) => { state.setValue(e.target.value); state.setError(''); }}
                    placeholder={placeholder}
                    data-autofocus
                />
            </Field>
        );
    }
    const help = state.reissue
        ? 'Printing again: the number is not issued a second time.'
        : count > 1
            ? `${count} invoices: numbered from this one on.`
            : (state.suggested ? 'The next free number - type a higher one to skip ahead.' : 'First invoice here: type the first number once; the rest follow on.');
    return (
        <div className="space-y-2">
            <Field label="Invoice number" help={help} error={state.error || undefined}>
                <Input
                    prefix={INVOICE_PREFIX}
                    value={state.value}
                    onChange={(e) => { state.setValue(e.target.value.replace(/^\s*ALWFQ-/i, '')); state.setError(''); }}
                    placeholder="e.g. 1001"
                    inputMode="numeric"
                    data-autofocus
                />
            </Field>
            <Checkbox
                label="Print again an invoice already issued"
                checked={state.reissue}
                onChange={(e) => { state.setReissue(e.target.checked); state.setError(''); }}
            />
        </div>
    );
}
